'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import AppLogo from '@/components/ui/AppLogo';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Calendar,
  Shield,
  Sun,
  AlertCircle,
  CheckCircle2,
  Copy,
  Check,
  Globe,
  Apple,
  Loader2,
} from 'lucide-react';

type LoginForm = { email: string; password: string; remember: boolean };
type SignupForm = {
  fullName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  birthYear: string;
  mode: 'adult' | 'child';
  pledge: boolean;
  terms: boolean;
};

const DEMO_ACCOUNTS = [
  { role: 'Adult User', email: 'mia@pureconnect.app', password: 'SunshineVibes2026' },
  { role: 'Child Account', email: 'kiddo.kai@pureconnect.app', password: 'HappyRainbow123' },
];

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<'adult' | 'child'>('adult');
  const [authError, setAuthError] = useState<string | null>(null);

  // Login form
  const {
    register: regLogin,
    handleSubmit: handleLogin,
    setValue: setLoginValue,
    formState: { errors: loginErrors },
  } = useForm<LoginForm>({ defaultValues: { remember: false } });

  // Signup form
  const {
    register: regSignup,
    handleSubmit: handleSignup,
    watch: watchSignup,
    setValue: setSignupValue,
    formState: { errors: signupErrors },
  } = useForm<SignupForm>({ defaultValues: { mode: 'adult' } });

  const signupPassword = watchSignup('password');

  const onLogin = async (data: LoginForm) => {
    setLoading(true);
    setAuthError(null);
    // Backend integration point: POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));
    const valid = DEMO_ACCOUNTS.find(
      (a) => a.email === data.email && a.password === data.password
    );
    if (!valid) {
      setAuthError('Invalid credentials — use the demo accounts below to sign in.');
      setLoading(false);
      return;
    }
    setLoading(false);
    setSuccess(true);
    setTimeout(() => {
      window.location.href = '/home-feed';
    }, 800);
  };

  const onSignup = async (_data: SignupForm) => {
    setLoading(true);
    // Backend integration point: POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1400));
    setLoading(false);
    setSuccess(true);
    setTimeout(() => {
      window.location.href = '/home-feed';
    }, 800);
  };

  const copyField = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const fillDemo = (account: (typeof DEMO_ACCOUNTS)[0]) => {
    setLoginValue('email', account.email);
    setLoginValue('password', account.password);
    setAuthError(null);
  };

  return (
    <div className="fade-in">
      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-2 mb-6">
        <AppLogo size={36} />
        <span className="font-extrabold text-xl text-primary">PureConnect</span>
      </div>

      {/* Tabs */}
      <div className="flex bg-muted rounded-2xl p-1 mb-6">
        {(['login', 'signup'] as const).map((t) => (
          <button
            key={`auth-tab-${t}`}
            onClick={() => { setTab(t); setAuthError(null); setSuccess(false); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
              tab === t
                ? 'bg-card shadow-sm text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      {success && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 mb-4 text-sm font-medium fade-in">
          <CheckCircle2 size={16} />
          {tab === 'login' ? 'Welcome back! Redirecting…' : 'Account created! Redirecting…'}
        </div>
      )}

      {authError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 mb-4 text-sm fade-in">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          {authError}
        </div>
      )}

      {/* LOGIN FORM */}
      {tab === 'login' && (
        <form onSubmit={handleLogin(onLogin)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="login-email" className="block text-sm font-semibold mb-1.5">
              Email address
            </label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                className="input-base pl-9"
                {...regLogin('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                })}
              />
            </div>
            {loginErrors.email && (
              <p className="text-xs text-danger mt-1.5 flex items-center gap-1">
                <AlertCircle size={12} />
                {loginErrors.email.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="login-password" className="block text-sm font-semibold">
                Password
              </label>
              <button type="button" className="text-xs text-primary font-medium hover:underline">
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Your password"
                className="input-base pl-9 pr-10"
                {...regLogin('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {loginErrors.password && (
              <p className="text-xs text-danger mt-1.5 flex items-center gap-1">
                <AlertCircle size={12} />
                {loginErrors.password.message}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="remember"
              type="checkbox"
              className="w-4 h-4 rounded border-border accent-primary"
              {...regLogin('remember')}
            />
            <label htmlFor="remember" className="text-sm text-muted-foreground">
              Remember me for 30 days
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || success}
            className="btn-primary w-full h-11 text-base"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Signing in…</>
            ) : success ? (
              <><CheckCircle2 size={16} /> Signed in!</>
            ) : (
              'Sign In to PureConnect'
            )}
          </button>

          {/* Social auth */}
          <div className="relative flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground font-medium">or continue with</span>
            <div className="flex-1 h-px bg-border" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" className="btn-outline h-10 text-sm gap-2">
              <Globe size={16} /> Google
            </button>
            <button type="button" className="btn-outline h-10 text-sm gap-2">
              <Apple size={16} /> Apple
            </button>
          </div>
        </form>
      )}

      {/* SIGNUP FORM */}
      {tab === 'signup' && (
        <form onSubmit={handleSignup(onSignup)} className="space-y-4" noValidate>
          {/* Account mode selector */}
          <div>
            <label className="block text-sm font-semibold mb-2">Account type</label>
            <div className="grid grid-cols-2 gap-3">
              {(['adult', 'child'] as const).map((mode) => (
                <button
                  key={`mode-${mode}`}
                  type="button"
                  onClick={() => { setSelectedMode(mode); setSignupValue('mode', mode); }}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-150 ${
                    selectedMode === mode
                      ? mode === 'adult' ?'border-primary bg-secondary/50 text-primary' :'border-child-safe bg-blue-50 text-child-safe' :'border-border bg-card text-muted-foreground hover:border-border/80 hover:bg-muted'
                  }`}
                >
                  {mode === 'adult' ? <Sun size={20} /> : <Shield size={20} />}
                  <span className="text-xs font-semibold">
                    {mode === 'adult' ? 'Adult Account' : 'Child Safe Mode'}
                  </span>
                </button>
              ))}
            </div>
            {selectedMode === 'child' && (
              <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-3 py-2.5 text-xs fade-in">
                <Shield size={13} className="mt-0.5 shrink-0" />
                <span>
                  Child Safe Mode requires a parent or guardian email for verification. Extra moderation and content filters will be active.
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold mb-1.5">Full name</label>
              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="fullName"
                  type="text"
                  placeholder="Your name"
                  className="input-base pl-9 text-sm"
                  {...regSignup('fullName', { required: 'Required' })}
                />
              </div>
              {signupErrors.fullName && (
                <p className="text-xs text-danger mt-1">{signupErrors.fullName.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="username" className="block text-sm font-semibold mb-1.5">Username</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
                <input
                  id="username"
                  type="text"
                  placeholder="username"
                  className="input-base pl-7 text-sm"
                  {...regSignup('username', {
                    required: 'Required',
                    pattern: { value: /^[a-z0-9_]{3,20}$/, message: '3–20 chars, lowercase' },
                  })}
                />
              </div>
              {signupErrors.username && (
                <p className="text-xs text-danger mt-1">{signupErrors.username.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="signup-email" className="block text-sm font-semibold mb-1.5">Email address</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="signup-email"
                type="email"
                placeholder="you@example.com"
                className="input-base pl-9"
                {...regSignup('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                })}
              />
            </div>
            {signupErrors.email && (
              <p className="text-xs text-danger mt-1 flex items-center gap-1">
                <AlertCircle size={11} />
                {signupErrors.email.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="signup-password" className="block text-sm font-semibold mb-1.5">Password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 8 chars"
                  className="input-base pl-9 pr-9 text-sm"
                  {...regSignup('password', {
                    required: 'Required',
                    minLength: { value: 8, message: 'Min 8 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
              {signupErrors.password && (
                <p className="text-xs text-danger mt-1">{signupErrors.password.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-semibold mb-1.5">Confirm</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="confirm-password"
                  type={showConfirmPw ? 'text' : 'password'}
                  placeholder="Repeat password"
                  className="input-base pl-9 pr-9 text-sm"
                  {...regSignup('confirmPassword', {
                    required: 'Required',
                    validate: (v) => v === signupPassword || 'Passwords must match',
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPw(!showConfirmPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle confirm password visibility"
                >
                  {showConfirmPw ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
              {signupErrors.confirmPassword && (
                <p className="text-xs text-danger mt-1">{signupErrors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="birthYear" className="block text-sm font-semibold mb-1.5">
              Birth year
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">Used for age-appropriate content settings</p>
            <div className="relative">
              <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="birthYear"
                type="number"
                placeholder="e.g. 1995"
                min={1920}
                max={2020}
                className="input-base pl-9"
                {...regSignup('birthYear', {
                  required: 'Birth year is required',
                  min: { value: 1920, message: 'Enter a valid year' },
                  max: { value: 2020, message: 'Must be born before 2021' },
                })}
              />
            </div>
            {signupErrors.birthYear && (
              <p className="text-xs text-danger mt-1 flex items-center gap-1">
                <AlertCircle size={11} />
                {signupErrors.birthYear.message}
              </p>
            )}
          </div>

          {/* Positivity pledge */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-2.5">
              <input
                id="pledge"
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-primary"
                {...regSignup('pledge', { required: 'You must accept the positivity pledge' })}
              />
              <div>
                <label htmlFor="pledge" className="text-sm font-semibold text-amber-900 cursor-pointer">
                  The PureConnect Positivity Pledge ☀️
                </label>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  I agree to share only uplifting, kind, and positive content. I will not post negativity, hate, or harmful material. I understand violations result in account review.
                </p>
              </div>
            </div>
            {signupErrors.pledge && (
              <p className="text-xs text-danger mt-2 flex items-center gap-1">
                <AlertCircle size={11} />
                {signupErrors.pledge.message}
              </p>
            )}
          </div>

          {/* Terms */}
          <div className="flex items-start gap-2">
            <input
              id="terms"
              type="checkbox"
              className="mt-0.5 w-4 h-4 accent-primary"
              {...regSignup('terms', { required: 'You must accept the terms' })}
            />
            <label htmlFor="terms" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
              I agree to PureConnect's{' '}
              <button type="button" className="text-primary font-semibold hover:underline">Terms of Service</button>
              {' '}and{' '}
              <button type="button" className="text-primary font-semibold hover:underline">Privacy Policy</button>.
              Users under 13 require parental consent.
            </label>
          </div>
          {signupErrors.terms && (
            <p className="text-xs text-danger -mt-2 flex items-center gap-1">
              <AlertCircle size={11} />
              {signupErrors.terms.message}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || success}
            className="btn-primary w-full h-11 text-base"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Creating account…</>
            ) : success ? (
              <><CheckCircle2 size={16} /> Account created!</>
            ) : (
              'Join PureConnect'
            )}
          </button>
        </form>
      )}

      {/* Demo credentials */}
      {tab === 'login' && (
        <div className="mt-5 card-base p-4 bg-secondary/30 fade-in">
          <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
            Demo Accounts
          </p>
          <div className="space-y-2">
            {DEMO_ACCOUNTS.map((account) => (
              <div
                key={`demo-${account.role}`}
                className="flex items-center gap-3 bg-card rounded-xl px-3 py-2.5 border border-border"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-foreground">{account.role}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{account.email}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => copyField(account.email, `email-${account.role}`)}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                    aria-label={`Copy email for ${account.role}`}
                  >
                    {copiedField === `email-${account.role}` ? (
                      <Check size={12} className="text-positive" />
                    ) : (
                      <Copy size={12} className="text-muted-foreground" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => fillDemo(account)}
                    className="text-[11px] font-semibold text-primary bg-secondary px-2.5 py-1 rounded-lg hover:bg-secondary/80 transition-colors btn-scale"
                  >
                    Use
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground mt-4">
        {tab === 'login' ? "Don't have an account? " : 'Already a member? '}
        <button
          type="button"
          onClick={() => setTab(tab === 'login' ? 'signup' : 'login')}
          className="text-primary font-semibold hover:underline"
        >
          {tab === 'login' ? 'Create one free' : 'Sign in'}
        </button>
      </p>
    </div>
  );
}